import React, { useEffect, useState } from 'react';
import '@/assets/styles/views/got-it/instant-meetings.scss';
import ringtoneUrl from '@/assets/sound/ringtone.mp3';
import InstantMeetingsDefaultState from '@/screens/got-it/instant-meetings/states/default.tsx';
import InstantMeetingsSearchingState from '@/screens/got-it/instant-meetings/states/searching.tsx';
import InstantMeetingsFoundState from '@/screens/got-it/instant-meetings/states/found.tsx';
import { useMeeting } from '@/context/meeting-context';
import { CALL_SERVICE_TYPES, CALL_STATE_TYPES } from '@/constants/socket.ts';
import { formatTimeHHmm, isNowInRange } from '@/utility';
import { useServerTimeStore } from '@/zustand/server-time/store.ts';
import { useApi } from '@/api/useApi.tsx';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { AvailableHourResponse } from '@dopingtech/live_expert_bff_api_ts';

const InstantCall = () => {
	const { callState, handleSearchUser, onCancelSearch, isSearchLoading } = useMeeting();
	const [isSearchDisabled, setIsSearchDisabled] = useState<boolean>(false);
	const [availableHours, setAvailableHours] = useState<AvailableHourResponse>({});

	const { data: serverTimeData } = useServerTimeStore();
	const { AvailableHourControllerApi, PoolControllerApi } = useApi();

	const { data: availableHoursData } = useQueryProvider([QUERY_KEYS.searchAvailableHours], () => AvailableHourControllerApi.searchAvailableHours(), {
		select: ({ data }) => data.data,
		enabled: true
	});

	const { data: waitingStudent } = useQueryProvider(
		[QUERY_KEYS.getExpertCounterpartCountLookingForMatch],
		() => PoolControllerApi.getExpertCounterpartCountLookingForMatch(CALL_SERVICE_TYPES.GOT_IT),
		{
			select: ({ data }) => data.data,
			enabled: true
		}
	);

	useEffect(() => {
		if (callState !== CALL_STATE_TYPES.FOUND) return;

		const audio = new Audio(ringtoneUrl);
		audio.loop = true;
		audio.play().catch(() => undefined);

		return () => {
			audio.pause();
			audio.currentTime = 0;
		};
	}, [callState]);

	useEffect(() => {
		const activeData = availableHoursData?.find(data => data?.liveServiceGroup === CALL_SERVICE_TYPES.GOT_IT);
		if (activeData) {
			const isActive = isNowInRange({
				nowTime: serverTimeData?.serverTime,
				startTime: activeData?.startTime,
				endTime: activeData?.endTime
			});

			setIsSearchDisabled(!isActive);
			setAvailableHours(activeData);
		}
	}, [availableHoursData]);

	if (callState !== CALL_STATE_TYPES.DEFAULT && callState === CALL_STATE_TYPES.CONNECT) {
		return (
			<section className={'container'}>
				<InstantMeetingsDefaultState
					isSearchDisabled={true}
					isSearchLoading={false}
					onSearchUser={() => handleSearchUser({ serviceGroup: CALL_SERVICE_TYPES.GOT_IT })}
				/>
			</section>
		);
	}

	return (
		<section className="container">
			{callState === CALL_STATE_TYPES.DEFAULT && (
				<InstantMeetingsDefaultState
					onSearchUser={() => handleSearchUser({ serviceGroup: CALL_SERVICE_TYPES.GOT_IT })}
					tooltipText={`Özel ders görüşmelerini ${formatTimeHHmm(availableHours?.startTime)} - ${formatTimeHHmm(availableHours?.endTime)} saatleri arasında yapabilirsin.`}
					isSearchDisabled={isSearchDisabled}
					isSearchLoading={isSearchLoading}
					waitingStudent={waitingStudent || 0}
				/>
			)}

			{callState === CALL_STATE_TYPES.SEARCHING && (
				<InstantMeetingsSearchingState
					onStopSearchUser={onCancelSearch}
					waitingStudent={waitingStudent || 0}
				/>
			)}

			{callState === CALL_STATE_TYPES.FOUND && <InstantMeetingsFoundState />}
		</section>
	);
};

export default InstantCall;
